import { PaymentMethod } from '@stripe/stripe-js'
import { BASKET_TYPES } from 'src/constants/global'

interface fetchServiceProps {
  url: string
  method?: 'GET' | 'PUT' | 'POST' | 'DELETE'
  body?: object
  headers?: object
  options?: object
  // see https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch for more examples
}

interface fetchServiceReturn {
  response?: object
  error?: {
    name: string
    message: string
    status?: number
    remaining_amount?: number
    fraud_rule_tripped?: string
  }
}

interface LmnMerchantInfoReturn extends fetchServiceReturn {
  response?: {
    MerchantId: number
    MerchantUuid: string
    lmn_conditions: string
    lmn_contraindications: string
  }
  error?: {
    name: string
    message: string
    status?: number
    remaining_amount?: number
    fraud_rule_tripped?: string
  }
}

interface PaymentsDialogItemProps {
  name: string
  subtotal: number
  subtotal_float: number
  tax: number
  tax_float: number
  total: number
  total_float: number
  discount: number
  discount_float: number
  quantity: number
  tags: string[]
}

interface Subscription {
  id: number
  name: string
  description: string
  short_description: string
  sku: string
  parent_id: string
  price: number
  eligible: boolean
  lmn_provider: string
  cart_based_eligibility: boolean
  exclude_from_shipping: boolean
  needs_review: boolean
  merchant_id: number
  merchant_product_id: string
  subscription: string
  is_free_trail_enabled: boolean
}

type BasketType = typeof BASKET_TYPES.FULLY_LMN | typeof BASKET_TYPES.FULLY_ELIGIBLE
  | typeof BASKET_TYPES.MIXED_BASKET | typeof BASKET_TYPES.MIXED_BASKET_WITH_LMN
  | typeof BASKET_TYPES.FULLY_INELIGIBLE

interface UserSubscription {
  email: string
  merchant_product_id: number
  type: string
  status: string
  product_name: string
}
interface getOrderServiceReturn extends fetchServiceReturn {
  response?: {
    amount: number
    currency: string
    basket_type: BasketType
    billing_address: {
      address_line_1: string
      address_line_2: string
      city: string
      country: string
      created_at: string
      first_name: string
      last_name: string
      postcode: string
      state: string
      updated_at: string
    }
    buyer_email: string
    shipping_price: number
    shipping_tax: number
    shipping_discount: number
    shipping_total: number
    eligible_total: number
    eligible_total_float: number
    eligible_subtotal: number
    eligible_subtotal_float: number
    eligible_shipping_subtotal: number
    eligible_shipping_subtotal_float: number
    eligible_shipping_tax: number
    eligible_shipping_tax_float: number
    eligible_tax: number
    eligible_tax_float: number
    eligible_discount: number
    eligible_discount_float: number
    ineligible_total: number
    ineligible_total_float: number
    ineligible_subtotal: number
    ineligible_subtotal_float: number
    ineligible_shipping_subtotal: number
    ineligible_shipping_subtotal_float: number
    ineligible_shipping_tax: number
    ineligible_shipping_tax_float: number
    ineligible_tax: number
    ineligible_tax_float: number
    status: string
    ineligible_discount: number
    ineligible_discount_float: number
    lmn_eligible_total: number
    lmn_eligible_total_float: number
    lmn_eligible_subtotal: number
    lmn_eligible_subtotal_float: number
    lmn_eligible_shipping_subtotal: number
    lmn_eligible_shipping_subtotal_float: number
    lmn_eligible_shipping_tax: number
    lmn_eligible_shipping_tax_float: number
    lmn_eligible_tax: number
    lmn_eligible_tax_float: number
    lmn_eligible_discount: number
    lmn_eligible_discount_float: number
    total: number
    total_float: number
    breakdown: {
      eligible: PaymentsDialogItemProps[]
      ineligible: PaymentsDialogItemProps[]
      lmn_eligible: PaymentsDialogItemProps[]
    }
    created_at: number
    updated_at: number
    merchant: {
      name: string
      logo: string
      lmn_provider: string
      uuid: string
      processor: string
    }
    subscription: Subscription[]
    user_subscription?: UserSubscription[]
  }
}
interface getOrderProcessorInfoServiceReturn extends fetchServiceReturn {
  response?: {
    session_processor: string
    session_details: {
      publishable_key: string
      eligible_order_client_secret: string
      ineligible_order_client_secret: string
    }
  }
}

interface getRainforestOrderProcessorInfoServiceReturn extends fetchServiceReturn {
  response?: {
    session_processor: string
    session_details: {
      ineligible_payin_config_id: string
      ineligible_session_key: string
      eligible_payin_config_id: string
      eligible_session_key: string
      fully_ineligible_payin_config_id: string
      fully_ineligible_session_key: string
    }
  }
}
interface createPartialProcessorServiceReturn extends fetchServiceReturn {
  response?: {
    session_processor: string
    session_details: {
      ineligible_payin_config_id: string
      ineligible_session_key: string
    }
  }
}

interface getMerchantLmnInfoReturn extends fetchServiceReturn {
  response?: {
    merchant_name: string
    merchant_logo: string
    merchant_uuid: string
    lmn_conditions?: string[]
    lmn_contraindications?: string[]
    lmn_provider?: string
    processor?: string
  }
}

interface getMerchantInfoReturn extends fetchServiceReturn {
  response?: {
    uuid: string
    name: string
    processor_status: string
    enabled: boolean
    status: string
    shopify_store_name: string
    modal_logo: string
    last_product_sync_at: string
    website: string
  }
}
interface checkLmnStatusBelugaReturn extends fetchServiceReturn {
  response?: {
    status: string | null
    approved_on: bigint | null
  }
}
interface getPaymentMethodByIdServiceReturn extends fetchServiceReturn {
  response?: PaymentMethod.Card
}

interface getNewClientSecretServiceReturn extends fetchServiceReturn {
  response?: {
    client_secret: string
    card_type: string
  }
}

interface getCardInfoServiceReturn extends fetchServiceReturn {
  response?: {
    setup_intent: {
      id: string
      client_secret: string
      created_at: bigint
      customer: string
    }
    processor: {
      session_processor: string
      session_details: {
        publishable_key: string
      }
    }
    customer: {
      id: string
      uuid: string
      created_at: bigint
      updated_at: bigint
      deleted_at: bigint
      user_id: string
      merchant_id: string
      stripe_customer_id: string
    }
  }
}

async function fetchService ({
  url,
  method,
  body,
  headers,
  options,
}: fetchServiceProps): Promise<
  fetchServiceReturn &
  getOrderServiceReturn &
  getOrderProcessorInfoServiceReturn &
  getRainforestOrderProcessorInfoServiceReturn &
  getCardInfoServiceReturn &
  getPaymentMethodByIdServiceReturn &
  getNewClientSecretServiceReturn &
  checkLmnStatusBelugaReturn &
  getMerchantInfoReturn &
  getMerchantLmnInfoReturn &
  LmnMerchantInfoReturn
  > {
  // see https://web.dev/fetch-api-error-handling/
  let response
  let error

  try {
    response = await fetch(url, {
      credentials: 'include',
      ...options,
      ...(method && { method }),
      ...(body && { body: JSON.stringify(body) }),
      ...(headers && {
        headers: { 'Content-Type': 'application/json', ...headers },
      }),
    })
  } catch (e) {
    error = {
      name: e.name,
      message: e.message,
    }
    return { error }
  }

  try {
    const data = await response?.json()

    if (!response?.ok) {
      // ON ERROR
      error = {
        name: response?.statusText,
        message: data.message,
        status: response?.status,
      }
      return { error }
    }

    return { response: data }
  } catch (e) {
    // @ts-expect-error this typing will complain bc the response may vary
    return { response }
  }
}

export {
  checkLmnStatusBelugaReturn,
  fetchService,
  LmnMerchantInfoReturn,
  fetchServiceReturn,
  getOrderServiceReturn,
  getOrderProcessorInfoServiceReturn,
  getRainforestOrderProcessorInfoServiceReturn,
  getPaymentMethodByIdServiceReturn,
  getMerchantLmnInfoReturn,
  getMerchantInfoReturn,
  getNewClientSecretServiceReturn,
  getCardInfoServiceReturn,
  PaymentsDialogItemProps,
  BasketType,
  createPartialProcessorServiceReturn,
}
